import { apiFetch } from './client'
import { ApiError } from './errors'

/**
 * s62 / C3b-2 (ADR-0032; decisao de Marcelo 20/10/2026 11:56 "APROVADO A ; F0 faixas por empresa ; F0a bloqueia ;
 * F0b 1-12 ; F1-F6 como recomendado") -- FAIXAS e FUNCOES da empresa e a FUNCAO DO CARGO.
 *
 * Contrato (apps/api/routes/job_bands.py; permissoes de CARGO: job_architecture.job.read / .write):
 *   GET    /api/job-bands                 faixas em ordem, cada uma com as funcoes (job_ids = cargos com a funcao)
 *   POST   /api/job-bands                 {name, position?}          PATCH/DELETE /api/job-bands/{id}
 *   POST   /api/job-functions             {name, band_id}            PATCH/DELETE /api/job-functions/{id}
 *   PATCH  /api/jobs/{id}/function        {function_id | null}
 * Erros: 409 com `count` (faixa com funcoes / funcao com cargos), 409 nome repetido, 409 limite de 1..12, 422 nome.
 */

export interface JobFunction {
  id: string
  name: string
  bandId: string
  jobsCount: number
  jobIds: string[]
}

export interface JobBand {
  id: string
  name: string
  position: number
  functions: JobFunction[]
}

interface FunctionResp {
  id: string
  name: string
  band_id: string
  jobs_count?: number
  job_ids?: string[]
}

interface BandResp {
  id: string
  name: string
  position: number
  functions?: FunctionResp[]
}

const funcao = (f: FunctionResp): JobFunction => ({
  id: f.id,
  name: f.name,
  bandId: f.band_id,
  jobsCount: f.jobs_count ?? 0,
  jobIds: f.job_ids ?? [],
})

export async function fetchBands(): Promise<JobBand[]> {
  const r = await apiFetch<BandResp[]>('/api/job-bands')
  return r
    .map((b) => ({ id: b.id, name: b.name, position: b.position, functions: (b.functions ?? []).map(funcao) }))
    .sort((a, b) => a.position - b.position)
}

export const BANDS_QUERY_KEY = ['job-bands'] as const

const corpo = (o: unknown) => ({ method: 'POST', body: o })
const patch = (o: unknown) => ({ method: 'PATCH', body: o })

export const createBand = (name: string, position?: number) =>
  apiFetch<BandResp>('/api/job-bands', corpo(position === undefined ? { name } : { name, position }))
export const updateBand = (id: string, c: { name?: string; position?: number }) =>
  apiFetch<BandResp>(`/api/job-bands/${encodeURIComponent(id)}`, patch(c))
export const deleteBand = (id: string) => apiFetch<void>(`/api/job-bands/${encodeURIComponent(id)}`, { method: 'DELETE' })
export const createFunction = (name: string, bandId: string) =>
  apiFetch<FunctionResp>('/api/job-functions', corpo({ name, band_id: bandId }))
export const updateFunction = (id: string, c: { name?: string; bandId?: string }) =>
  apiFetch<FunctionResp>(
    `/api/job-functions/${encodeURIComponent(id)}`,
    patch({ ...(c.name !== undefined ? { name: c.name } : {}), ...(c.bandId !== undefined ? { band_id: c.bandId } : {}) }),
  )
export const deleteFunction = (id: string) =>
  apiFetch<void>(`/api/job-functions/${encodeURIComponent(id)}`, { method: 'DELETE' })
export const setJobFunction = (jobId: string, functionId: string | null) =>
  apiFetch<unknown>(`/api/jobs/${encodeURIComponent(jobId)}/function`, patch({ function_id: functionId }))

/** job_id -> funcao gravada no cargo (vem de `job_ids` de cada funcao). */
export function functionByJob(bands: readonly JobBand[]): Map<string, JobFunction> {
  const m = new Map<string, JobFunction>()
  for (const b of bands) for (const f of b.functions) for (const j of f.jobIds) m.set(j, f)
  return m
}

const palavras = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w !== '')

/** "coordenadora" casa com "coordenador"; "operadores" nao casa com "operador" (so 1 letra de folga: genero). */
const casa = (titulo: string, funcaoW: string) =>
  titulo === funcaoW || (titulo.startsWith(funcaoW) && titulo.length - funcaoW.length === 1)

/**
 * F2 -- funcao SUGERIDA pelo titulo do cargo: a funcao cujas palavras aparecem SEGUIDAS no titulo. Vence a de mais
 * palavras ("Gerente Executivo Industrial" -> Gerente Executivo, nao Gerente); empate -> a que aparece primeiro no
 * titulo ("Assistente Tecnico" -> Assistente). Sem nenhuma -> null (a tela pede para escolher).
 */
export function suggestFunction<T extends { name: string }>(jobName: string, functions: readonly T[]): T | null {
  const t = palavras(jobName)
  let melhor: { f: T; n: number; pos: number } | null = null
  for (const f of functions) {
    const w = palavras(f.name)
    if (w.length === 0) continue
    for (let i = 0; i + w.length <= t.length; i++) {
      if (w.every((p, k) => casa(t[i + k], p))) {
        if (!melhor || w.length > melhor.n || (w.length === melhor.n && i < melhor.pos)) melhor = { f, n: w.length, pos: i }
        break
      }
    }
  }
  return melhor?.f ?? null
}

/** Corpo do erro da API: `code` (ex.: band_has_functions) e `count` do 409 de exclusao bloqueada. */
export function catalogError(e: unknown): { code: string | null; count: number | null } {
  const d = e instanceof ApiError && e.detail && typeof e.detail === 'object' ? (e.detail as Record<string, unknown>) : null
  return { code: typeof d?.code === 'string' ? d.code : null, count: typeof d?.count === 'number' ? d.count : null }
}

/** s69 / 2w (Marcelo 29/10/2026 17:07): a funcao aparece em MAIUSCULA, como o cargo e a area (so na tela; o nome gravado nao muda). */
export const maiuscula = (s: string) => s.toLocaleUpperCase('pt-BR')
